import React, { useState } from 'react';
import { ExclamationTriangleIcon, EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { supportAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface DisabledAccountBannerProps {
  user: any;
}

const DisabledAccountBanner: React.FC<DisabledAccountBannerProps> = ({ user }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState('Account Unblock Appeal Request');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!user?.is_disabled) {
    return null;
  }

  const handleSubmitUnblockTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please provide details for your unblock appeal.');
      return;
    }

    setSubmitting(true);
    try {
      await supportAPI.createUnblockTicket({
        subject,
        message: message.trim(),
      });
      toast.success('Your unblock ticket has been submitted to administration.');
      setIsModalOpen(false);
      setMessage('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit unblock ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 text-white px-4 py-3 shadow-lg">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <ExclamationTriangleIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-extrabold text-sm uppercase tracking-wide">
                Account Status Notice: Disabled
              </p>
              <p className="text-xs text-white/90 font-medium">
                Your profile & items are currently hidden from public view. Submit an appeal ticket to request unblocking. Check your email for the reason.
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-5 py-2 bg-white text-red-700 hover:bg-red-50 font-black text-xs uppercase tracking-wider rounded-xl shadow-md transition-all hover:scale-105 active:scale-95 flex items-center space-x-2"
          >
            <EnvelopeIcon className="h-4 w-4 text-red-600" />
            <span>Raise Unblock Ticket</span>
          </button>
        </div>
      </div>

      {/* Unblock Appeal Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 dark:border-gray-800 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-orange-100 dark:bg-orange-950/40 flex items-center justify-center text-orange-600">
                <ExclamationTriangleIcon className="h-7 w-7" />
              </div>
              <div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">
                  Request Account Unblock
                </h3>
                <p className="text-xs text-gray-500 font-medium">
                  Submit an appeal to administration detailing why your account should be restored.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmitUnblockTicket} className="space-y-5">
              <div>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none text-gray-900 dark:text-white font-medium text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-2">
                  Appeal Details & Justification
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Explain why your account should be re-enabled and any context administration should know..."
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none text-gray-900 dark:text-white font-medium text-sm resize-none"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-wider hover:opacity-90 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Appeal Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default DisabledAccountBanner;
